import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  Paper,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Search, FilterList, Clear } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import TextField from './UIKit/TextField';
import Select from './UIKit/Select';
import Button from './UIKit/Button';
import { useTranslation } from 'react-i18next';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';

const FilterContainer = styled(Paper)(() => ({
  padding: '16px',
  marginBottom: '24px',
  borderRadius: '12px',
}));

const FilterHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
});


const LearningPathFilters = ({ onFilterChange }) => {
  const { t } = useTranslation(['learningPaths', 'common']);
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Получаем профессии и состояние загрузки из Redux
  const availableProfessions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);

  useEffect(() => {
    dispatch(fetchProfessions({ all: true, isCommon:1 }));
  }, [dispatch]);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleDifficultyChange = useCallback((e) => {
    setDifficulty(e.target.value);
  }, []);

  const handleProfessionsChange = useCallback((event, newValue) => {
    setSelectedProfessions(Array.isArray(newValue) ? newValue : []);
  }, []);

  const handleDeleteProfession = useCallback((indexToDelete) => {
    setSelectedProfessions(prev => {
      const newProfessions = [...prev];
      newProfessions.splice(indexToDelete, 1);
      return newProfessions;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setDifficulty('');
    setSelectedProfessions([]);
    onFilterChange({
      search: '',
      difficulty: '',
      professions: [],
    });
  }, [onFilterChange]);

  const handleSubmit = useCallback(() => {
    onFilterChange({
      search,
      difficulty,
      professions: selectedProfessions.map((prof) => prof.id),
    });
  }, [search, difficulty, selectedProfessions, onFilterChange]);

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  return (
    <FilterContainer elevation={0}>
      <FilterHeader>
        <Typography variant="h6" fontWeight="bold">
          {t('filters.title')}
        </Typography>
        <IconButton onClick={toggleFilters}>
          <FilterList color={showFilters ? 'primary' : 'inherit'} />
        </IconButton>
      </FilterHeader>

      <Stack spacing={2}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('filters.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {showFilters && (
          <>
            <Box>
              <FormControl fullWidth variant="outlined">
                <Select
                  value={difficulty}
                  onChange={handleDifficultyChange}
                  label={t('filters.difficulty')}
                  placeholder={t('filters.difficulty')}
                >
                  <MenuItem value="">
                    {t('filters.allLevels')}
                  </MenuItem>
                  <MenuItem value="beginner">
                    {t('common:difficulty.beginner')}
                  </MenuItem>
                  <MenuItem value="intermediate">
                    {t('common:difficulty.intermediate')}
                  </MenuItem>
                  <MenuItem value="advanced">
                    {t('common:difficulty.advanced')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('filters.professions')}
              </Typography>
              <Autocomplete
                fullWidth
                multiple
                options={availableProfessions}
                getOptionLabel={(option) =>
                  option.library && option.library.name
                    ? option.library.name
                    : option.name || ''
                }
                value={selectedProfessions}
                onChange={handleProfessionsChange}
                isOptionEqualToValue={(option, value) =>
                  (option.id && value.id && option.id === value.id) ||
                  (option._id && value._id && option._id === value._id)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={
                        option.library && option.library.name
                          ? option.library.name
                          : option.name || ''
                      }
                      {...getTagProps({ index })}
                      key={option.id || index}
                      color="primary"
                      onDelete={(e) => {
                        e.stopPropagation();
                        handleDeleteProfession(index);
                      }}
                      size="small"
                      sx={{ m: 0.3 }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedProfessions.length > 0
                        ? ''
                        : professionsLoading
                        ? t('common:loading')
                        : t('filters.selectProfessions')
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {professionsLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={professionsLoading}
                disableCloseOnSelect
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 'auto',
                    flexWrap: 'wrap',
                  },
                }}
              />
            </Box>
          </>
        )}

        <Box display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearFilters}
            disabled={
              !search && !difficulty && selectedProfessions.length === 0
            }
          >
            {t('common:actions.clear')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            {t('filters.apply')}
          </Button>
        </Box>
      </Stack>
    </FilterContainer>
  );
};

export default LearningPathFilters;
